import React, { useRef, useState } from 'react';
import { Upload, X, ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../lib/cn';
import { Button } from '../ui/Button';
import { useUploadLogo, useUpdateBranding } from '../../hooks/queries/useTenantBranding';

interface LogoUploaderProps {
  currentLogoUrl: string | null;
}

const MAX_SIZE_BYTES = 5 * 1024 * 1024; // 5 MB
const ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'image/svg+xml', 'image/webp'];

export function LogoUploader({ currentLogoUrl }: LogoUploaderProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const uploadLogo = useUploadLogo();
  const updateBranding = useUpdateBranding();

  const isPending = uploadLogo.isPending || updateBranding.isPending;

  function validateFile(file: File): string | null {
    if (!ACCEPTED_TYPES.includes(file.type)) {
      return 'Format non supporté. Utilisez PNG, JPEG, SVG ou WebP.';
    }
    if (file.size > MAX_SIZE_BYTES) {
      return 'Fichier trop volumineux. Maximum 5 Mo.';
    }
    return null;
  }

  async function handleFile(file: File) {
    const error = validateFile(file);
    if (error) {
      toast.error(error);
      return;
    }

    try {
      const url = await uploadLogo.mutateAsync(file);
      await updateBranding.mutateAsync({ logo_url: url });
      toast.success('Logo mis à jour');
    } catch {
      toast.error('Erreur lors du téléversement du logo');
    }
  }

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    // Reset so same file can be re-selected
    e.target.value = '';
  }

  function handleDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  }

  function handleDragOver(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setIsDragging(true);
  }

  function handleDragLeave() {
    setIsDragging(false);
  }

  async function handleRemoveLogo() {
    try {
      await updateBranding.mutateAsync({ logo_url: null });
      toast.success('Logo supprimé');
    } catch {
      toast.error('Erreur lors de la suppression du logo');
    }
  }

  return (
    <div className="space-y-4">
      <p className="text-sm font-semibold text-neutral-700">Logo du cabinet</p>

      {/* Current logo preview */}
      {currentLogoUrl ? (
        <div className="flex items-center gap-4 p-3 bg-neutral-50 rounded-cime border border-neutral-200">
          <img
            src={currentLogoUrl}
            alt="Logo du cabinet"
            className="h-20 object-contain max-w-[200px]"
          />
          <Button
            variant="ghost"
            size="sm"
            onClick={handleRemoveLogo}
            isLoading={updateBranding.isPending}
            className="text-red-500 hover:text-red-600 hover:bg-red-50 ml-auto shrink-0"
          >
            <X className="w-4 h-4" aria-hidden />
            Supprimer le logo
          </Button>
        </div>
      ) : (
        <div className="flex items-center justify-center h-20 w-[200px] bg-neutral-100 rounded-cime border border-dashed border-neutral-300">
          <ImageIcon className="w-8 h-8 text-neutral-400" aria-hidden />
        </div>
      )}

      {/* Dropzone */}
      <div
        role="button"
        tabIndex={0}
        aria-label="Zone de téléversement du logo — cliquez ou glissez un fichier"
        onClick={() => !isPending && fileInputRef.current?.click()}
        onKeyDown={(e) => {
          if ((e.key === 'Enter' || e.key === ' ') && !isPending) {
            fileInputRef.current?.click();
          }
        }}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        className={cn(
          'relative flex flex-col items-center justify-center gap-2 p-6',
          'border-2 border-dashed rounded-cime cursor-pointer transition-colors',
          'text-sm text-neutral-500',
          isDragging
            ? 'border-cime-primary bg-cime-primary/5 text-cime-primary'
            : 'border-neutral-300 hover:border-cime-primary/50 hover:bg-neutral-50',
          isPending && 'opacity-60 cursor-not-allowed',
        )}
      >
        {isPending ? (
          <span
            className="w-5 h-5 rounded-full border-2 border-cime-primary border-t-transparent animate-spin"
            aria-hidden
          />
        ) : (
          <Upload className="w-5 h-5" aria-hidden />
        )}
        <span>
          {isPending
            ? 'Téléversement en cours…'
            : 'Glissez votre logo ici ou cliquez pour sélectionner'}
        </span>
        <span className="text-xs text-neutral-400">PNG, JPEG, SVG, WebP — max 5 Mo</span>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPTED_TYPES.join(',')}
        onChange={handleInputChange}
        className="sr-only"
        aria-hidden
        tabIndex={-1}
      />
    </div>
  );
}
